// Demo gallery — sample shapes the user can load with one click. Items are
// generated procedurally so the build does NOT need to ship binary STL assets.
// External STLs can still be added under public/demo/ and listed here.

import type { Triangle, Vec3 } from '../core/types';
import { buildVaseMesh, VASE_PRESETS } from '../core/vaseModel';

export interface DemoItem {
  id: string;
  name: string;
  description: string;
  /** Procedural shape generator OR remote STL URL — exactly one of these is set. */
  generator?: () => Triangle[];
  url?: string;
}

function makeCube(size: number): Triangle[] {
  const s = size / 2;
  const v = (x: number, y: number, z: number): Vec3 => [x, y, z];
  const A = v(-s, -s, 0),  B = v(s, -s, 0),  C = v(s, s, 0),  D = v(-s, s, 0);
  const E = v(-s, -s, size), F = v(s, -s, size), G = v(s, s, size), H = v(-s, s, size);
  const t = (a: Vec3, b: Vec3, c: Vec3, n: Vec3): Triangle => ({ a, b, c, normal: n });
  return [
    t(A, C, B, [0, 0, -1]), t(A, D, C, [0, 0, -1]),
    t(E, F, G, [0, 0, 1]),  t(E, G, H, [0, 0, 1]),
    t(A, B, F, [0, -1, 0]), t(A, F, E, [0, -1, 0]),
    t(D, H, G, [0, 1, 0]),  t(D, G, C, [0, 1, 0]),
    t(A, E, H, [-1, 0, 0]), t(A, H, D, [-1, 0, 0]),
    t(B, C, G, [1, 0, 0]),  t(B, G, F, [1, 0, 0])
  ];
}

function makeCylinder(radius: number, height: number, segments = 48): Triangle[] {
  const tris: Triangle[] = [];
  const ring = (z: number) => Array.from({ length: segments }, (_, i) => {
    const a = (i / segments) * Math.PI * 2;
    return [Math.cos(a) * radius, Math.sin(a) * radius, z] as Vec3;
  });
  const bot = ring(0), top = ring(height);
  for (let i = 0; i < segments; i++) {
    const j = (i + 1) % segments;
    tris.push({ a: bot[i], b: bot[j], c: top[j], normal: [Math.cos((i + 0.5) / segments * Math.PI * 2), Math.sin((i + 0.5) / segments * Math.PI * 2), 0] });
    tris.push({ a: bot[i], b: top[j], c: top[i], normal: [Math.cos((i + 0.5) / segments * Math.PI * 2), Math.sin((i + 0.5) / segments * Math.PI * 2), 0] });
  }
  // Caps
  const center0: Vec3 = [0, 0, 0], centerH: Vec3 = [0, 0, height];
  for (let i = 0; i < segments; i++) {
    const j = (i + 1) % segments;
    tris.push({ a: center0, b: bot[j], c: bot[i], normal: [0, 0, -1] });
    tris.push({ a: centerH, b: top[i], c: top[j], normal: [0, 0, 1] });
  }
  return tris;
}

export const DEMO_GALLERY: DemoItem[] = [
  { id: 'cube-50', name: 'Küp 50mm', description: 'Test küpü — slicer doğrulaması için',
    generator: () => makeCube(50) },
  { id: 'cyl-30x80', name: 'Silindir Ø60×80', description: 'Düz silindir — vazo modu testine ideal',
    generator: () => makeCylinder(30, 80) },
  { id: 'vase-classic', name: 'Klasik Vazo',
    description: 'Belly + twist preset — parametrik vazo örneği',
    generator: () => buildVaseMesh(VASE_PRESETS[0]?.params ?? {}) },
  { id: 'vase-tall', name: 'Uzun Vazo',
    description: 'Yüksek narrow profil',
    generator: () => buildVaseMesh({ height: 180, bottomRadius: 35, topRadius: 25, bellyRadius: 45, bellyHeight: 0.4, twist: 0 }) }
];

/** Resolve a gallery item to triangles. */
export async function loadDemo(item: DemoItem): Promise<Triangle[]> {
  if (item.generator) return item.generator();
  if (item.url) {
    const res = await fetch(item.url);
    if (!res.ok) throw new Error(`Demo yüklenemedi: ${item.name} (HTTP ${res.status})`);
    const buf = await res.arrayBuffer();
    const { parseStl } = await import('../core/mesh');
    return parseStl(buf);
  }
  throw new Error('Boş demo öğesi');
}
